import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Smartphone } from 'lucide-react';
import { SiAppstore } from 'react-icons/si';
import { useAuth } from '../../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import {
  peekSessionPromoReason,
  clearSessionPromoReason,
  isFirstPromoCompleted,
  markFirstPromoCompleted,
  shouldShowRandomPromo,
  markRandomPromoShown,
  isAuthenticatedAppPath,
  peekFirstPromoLock,
  setFirstPromoLock,
  clearFirstPromoLock,
  APP_STORE_URL,
  PLAY_STORE_URL
} from '../../utils/appDownloadPromoStorage';
import './AppDownloadPromo.css';

/** Official-style Google Play triangle (four colors). Paths from Grommet / Simple Icons geometry. */
function GooglePlayMark({ size = 44 }) {
  return (
    <svg
      className="app-download-promo-gp-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path
        fill="#00D9FF"
        d="M11.128 12.003 1.09 22.011a2.73 2.73 0 0 0 3.999 1.62l11.29-6.377-5.251-5.25Z"
      />
      <path
        fill="#00F076"
        d="m21.338 9.67-4.899-2.806-5.311 5.14 5.251 5.25 4.944-2.865a2.663 2.663 0 0 0 0-4.719h.015Z"
      />
      <path
        fill="#FFD23F"
        d="M1.09 1.995c-.06.225-.091.457-.09.69V21.32c0 .233.03.465.09.69l10.038-10.007L1.09 1.995Z"
      />
      <path
        fill="#FF1744"
        d="m11.128 12.003 5.311-5.139L5.126.383A2.79 2.79 0 0 0 3.723 0 2.73 2.73 0 0 0 1.09 1.988l10.038 10.015Z"
      />
    </svg>
  );
}

/**
 * Full-screen promo for native apps: first login / post-registration,
 * plus occasional random prompts for users who completed the first prompt.
 */
const AppDownloadPromo = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [variant, setVariant] = useState(/** @type {'register' | 'login' | 'random' | null} */ (null));
  const randomEvaluatedRef = useRef(false);

  useEffect(() => {
    randomEvaluatedRef.current = false;
  }, [user?.id]);

  const close = useCallback(() => {
    setOpen(false);
    setVariant(null);
  }, []);

  const onDismiss = useCallback(() => {
    if (user?.id && (variant === 'register' || variant === 'login')) {
      markFirstPromoCompleted(user.id);
      clearFirstPromoLock(user.id);
    }
    clearSessionPromoReason();
    close();
  }, [user?.id, variant, close]);

  const onStoreClick = useCallback(() => {
    if (user?.id && (variant === 'register' || variant === 'login')) {
      markFirstPromoCompleted(user.id);
      clearFirstPromoLock(user.id);
    }
    clearSessionPromoReason();
    close();
  }, [user?.id, variant, close]);

  useEffect(() => {
    if (!user?.id || !isAuthenticatedAppPath(pathname)) return;
    if (open) return;

    const reason = peekSessionPromoReason();
    const firstDone = isFirstPromoCompleted(user.id);
    const lockedVariant = peekFirstPromoLock(user.id);

    if (!firstDone && (lockedVariant || reason === 'register' || reason === 'login')) {
      const v = lockedVariant || reason;
      if (reason && !lockedVariant) {
        setFirstPromoLock(user.id, reason);
        clearSessionPromoReason();
      }
      setVariant(v);
      setOpen(true);
      return;
    }

    if (reason && firstDone) {
      clearSessionPromoReason();
    }

    if (!firstDone) return;
    if (randomEvaluatedRef.current) return;
    randomEvaluatedRef.current = true;

    if (shouldShowRandomPromo(user.id)) {
      markRandomPromoShown(user.id);
      setVariant('random');
      setOpen(true);
    }
  }, [user?.id, pathname, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onDismiss]);

  if (!open || !variant) return null;

  const title =
    variant === 'random'
      ? t('appDownloadPromoRandomTitle')
      : t('appDownloadPromoWelcomeTitle');
  const subtitle =
    variant === 'random'
      ? t('appDownloadPromoRandomSubtitle')
      : t('appDownloadPromoWelcomeSubtitle');

  const modal = (
    <div
      className="app-download-promo-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="app-download-promo-title"
    >
      <div
        className="app-download-promo-card"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className="app-download-promo-card__glow" aria-hidden />
        <button
          type="button"
          className="app-download-promo-close"
          onClick={onDismiss}
          aria-label={t('close')}
        >
          <X size={18} strokeWidth={2.5} />
        </button>

        <div className="app-download-promo-hero" aria-hidden>
          <div className="app-download-promo-hero__ring" />
          <div className="app-download-promo-hero__icon">
            <Smartphone size={28} strokeWidth={1.75} />
          </div>
        </div>

        <h2 id="app-download-promo-title" className="app-download-promo-title">
          {title}
        </h2>
        <p className="app-download-promo-subtitle">{subtitle}</p>

        <div className="app-download-promo-actions">
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="app-download-promo-store app-download-promo-store--ios"
            onClick={onStoreClick}
            aria-label={`${t('appStoreBadgeKicker')} ${t('appStoreBadgeTitle')}`}
          >
            <span className="app-download-promo-store__iconWrap">
              <SiAppstore className="app-download-promo-as-icon" aria-hidden />
            </span>
            <span className="app-download-promo-store__text">
              <span className="app-download-promo-store__kicker">{t('appStoreBadgeKicker')}</span>
              <span className="app-download-promo-store__name">{t('appStoreBadgeTitle')}</span>
            </span>
          </a>
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="app-download-promo-store app-download-promo-store--android"
            onClick={onStoreClick}
            aria-label={`${t('googlePlayBadgeKicker')} ${t('googlePlayBadgeTitle')}`}
          >
            <span className="app-download-promo-store__iconWrap">
              <GooglePlayMark size={46} />
            </span>
            <span className="app-download-promo-store__text">
              <span className="app-download-promo-store__kicker">{t('googlePlayBadgeKicker')}</span>
              <span className="app-download-promo-store__name">{t('googlePlayBadgeTitle')}</span>
            </span>
          </a>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default AppDownloadPromo;
